/**
 * Email Verification Page
 *
 * Verifies user's email address using token from URL query params.
 * Shows success/error message and provides link to login.
 * Handles expired/invalid tokens gracefully.
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Alert,
  Stack,
  Center,
  Loader,
} from '@mantine/core';
import { IconAlertCircle, IconCircleCheck } from '@tabler/icons-react';
import { buildApiUrl } from '@/api/config';

export const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setError('No verification token provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/api/auth/verify/${token}`), {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSuccess(true);
          setMessage(data.message || 'Email verified successfully');
        } else {
          const errorData = await response.json().catch(() => ({
            detail: 'Verification failed',
          }));
          setError(errorData.detail || 'Invalid or expired verification token');
        }
      } catch (err) {
        setError('Failed to verify email. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <Container maw={{ base: '100%', xs: 420 }} px={{ base: 'md', xs: 0 }} my={40}>
      <Center>
        <Stack gap="md" w="100%">
          <Title ta="center">Email Verification</Title>

          <Paper withBorder p={30} mt={30} radius="md">
            <Stack gap="md" align="center">
              {isLoading && (
                <>
                  <Loader size="lg" />
                  <Text c="dimmed" size="sm">
                    Verifying your email address...
                  </Text>
                </>
              )}

              {!isLoading && success && (
                <>
                  <Alert
                    icon={<IconCircleCheck size={16} />}
                    title="Success"
                    color="teal"
                    w="100%"
                  >
                    {message}
                  </Alert>

                  <Text size="sm" ta="center">
                    Your email has been verified. You can now sign in to your
                    account.
                  </Text>

                  <Button component={Link} to="/login" fullWidth>
                    Go to Login
                  </Button>
                </>
              )}

              {!isLoading && error && (
                <>
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Error"
                    color="red"
                    w="100%"
                  >
                    {error}
                  </Alert>

                  <Text size="sm" ta="center" c="dimmed">
                    The verification link may have expired or is invalid. Please
                    contact support for assistance.
                  </Text>

                  <Button component={Link} to="/login" variant="outline" fullWidth>
                    Back to Login
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Center>
    </Container>
  );
};
